import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { analyzeSign, type SignedInterval, type Sign } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { FUNCTION_AREA_DEFAULT_PARAMS, type FunctionAreaParams } from './function-area.model';

/**
 * Creates area visualization between a function and the x-axis.
 *
 * Features:
 * - Shaded area between the function curve and x-axis
 * - Vertical boundary lines at xStart and xEnd (dashed, indigo by default)
 * - Optional signed areas: positive regions (green) and negative regions (red)
 * - Handles reversed bounds (xStart > xEnd) by swapping and inverting sign colors
 *
 * Uses JSXGraph's integral element for the area visualization.
 * For signed areas, uses analyzeSign from analytics to find zero crossings
 * and creates separate integral elements for positive and negative regions.
 */
export class FunctionAreaCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'functionArea';
  override readonly computedValueKeys = ['area', 'signedArea', 'totalArea'] as const;

  /** Maximum number of signed intervals to display */
  private readonly maxIntervals = 20;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, featureTheme } = context;
    const params = def.params as FunctionAreaParams | undefined;

    if (!params?.of) {
      throw new Error('FunctionArea feature requires params.of to specify the parent function');
    }
    if (params.xStart === undefined || params.xEnd === undefined) {
      throw new Error('FunctionArea feature requires params.xStart and params.xEnd');
    }

    const parentFn = this.getEvaluableParent(params.of, context);
    const objects: GeometryElement[] = [];

    // Resolve xStart/xEnd via valueContext for reactive expression support
    // For numeric params, use mutable state that setParams can update
    // For expression params, use valueContext thunks (reactive, setParams is no-op)
    const { valueContext } = context;
    let mutableXStart = typeof params.xStart === 'number' ? params.xStart : 0;
    let mutableXEnd = typeof params.xEnd === 'number' ? params.xEnd : 0;

    const getXStart =
      typeof params.xStart === 'string' && valueContext ? valueContext.createThunk(params.xStart) : () => mutableXStart;

    const getXEnd =
      typeof params.xEnd === 'string' && valueContext ? valueContext.createThunk(params.xEnd) : () => mutableXEnd;

    // Helper to get ordered bounds and check if reversed
    const getBounds = () => {
      const xStart = getXStart();
      const xEnd = getXEnd();
      const reversed = xStart > xEnd;
      const xMin = reversed ? xEnd : xStart;
      const xMax = reversed ? xStart : xEnd;
      return { xMin, xMax, reversed };
    };

    // Helper to get effective sign (inverted if bounds are reversed)
    const getEffectiveSign = (sign: Sign, reversed: boolean): Sign => {
      if (!reversed) {
        return sign;
      }
      // Invert sign when bounds are reversed
      if (sign === 'positive') {
        return 'negative';
      }
      if (sign === 'negative') {
        return 'positive';
      }
      return sign;
    };

    const signed = params.signed ?? FUNCTION_AREA_DEFAULT_PARAMS.signed;

    // Resolve styles
    const polygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, []);
    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display, []);
    const pointStyle = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Resolve signed area styles
    const positivePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['positive']);
    const negativePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['negative']);

    // Create the function curve for JSXGraph integral element
    const fnCurve = board.create('functiongraph', [parentFn.evaluate], { visible: false }) as GeometryElement;
    objects.push(fnCurve);

    if (signed) {
      // For signed areas, create separate integrals for positive and negative regions
      // Cache intervals for performance
      let cachedIntervals: SignedInterval[] = [];
      let cacheKey = '';
      let cachedReversed = false;

      const getIntervals = (): { intervals: SignedInterval[]; reversed: boolean } => {
        const { xMin, xMax, reversed } = getBounds();
        const fnParams =
          'getParams' in parentFn ? (parentFn as { getParams: () => Record<string, number> }).getParams() : {};
        const paramsKey = Object.values(fnParams)
          .map((v) => v.toFixed(4))
          .join(',');
        const newKey = `${xMin.toFixed(2)},${xMax.toFixed(2)}|${paramsKey}`;

        if (newKey !== cacheKey) {
          cacheKey = newKey;
          cachedReversed = reversed;
          cachedIntervals = analyzeSign(parentFn.evaluate, xMin, xMax, {
            maxResults: this.maxIntervals,
          });
        }
        return { intervals: cachedIntervals, reversed: cachedReversed };
      };

      const getInterval = (index: number): { interval: SignedInterval | null; reversed: boolean } => {
        const { intervals, reversed } = getIntervals();
        return {
          interval: index < intervals.length ? intervals[index] : null,
          reversed,
        };
      };

      // Create placeholder integrals for each potential interval
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        const integral = board.create(
          'integral',
          [[() => getInterval(index).interval?.start ?? NaN, () => getInterval(index).interval?.end ?? NaN], fnCurve],
          {
            fillColor: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 'transparent';
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillColor ? context.themeService.resolveColorToken(style.fillColor) : 'transparent';
            },
            fillOpacity: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 0;
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillOpacity ?? 0.3;
            },
            visible: () => {
              const { interval } = getInterval(index);
              return visible && interval !== null;
            },
            withLabel: false,
            curveLeft: { visible: false },
            curveRight: { visible: false },
            baseLeft: { visible: false },
            baseRight: { visible: false },
            label: { visible: false },
          },
        ) as GeometryElement;

        objects.push(integral);
      }
    } else {
      // Single unsigned area - use reactive functions for bounds
      const fillColor = context.themeService.resolveColorToken(polygonStyle.fillColor ?? 'indigo');

      const integral = board.create('integral', [[() => getBounds().xMin, () => getBounds().xMax], fnCurve], {
        fillColor,
        fillOpacity: polygonStyle.fillOpacity ?? 0.3,
        visible,
        withLabel: false,
        curveLeft: { visible: false },
        curveRight: { visible: false },
        baseLeft: { visible: false },
        baseRight: { visible: false },
        label: { visible: false },
      }) as GeometryElement;

      objects.push(integral);
    }

    // Create boundary lines at xStart and xEnd using elementFactory
    const boundaryPointStyle = {
      color: pointStyle.color ?? 'indigo',
      visible: pointStyle.visible ?? false,
      fixed: true,
      size: 3,
    };
    const boundaryLineStyle = {
      color: lineStyle.color ?? 'indigo',
      dash: lineStyle.dash ?? 3,
      strokeWidth: lineStyle.strokeWidth ?? 2,
      visible,
    };

    // Left boundary line (xStart) - use reactive functions
    const leftLineResult = context.elementFactory.line({
      a: [() => getXStart(), 0],
      b: [() => getXStart(), () => parentFn.evaluate(getXStart())],
      style: {
        ...boundaryLineStyle,
        straightFirst: false,
        straightLast: false,
        vertex: boundaryPointStyle,
      },
    });
    objects.push(leftLineResult.a, leftLineResult.b, leftLineResult.line);

    // Right boundary line (xEnd) - use reactive functions
    const rightLineResult = context.elementFactory.line({
      a: [() => getXEnd(), 0],
      b: [() => getXEnd(), () => parentFn.evaluate(getXEnd())],
      style: {
        ...boundaryLineStyle,
        straightFirst: false,
        straightLast: false,
        vertex: boundaryPointStyle,
      },
    });
    objects.push(rightLineResult.a, rightLineResult.b, rightLineResult.line);

    // Trapezoidal rule integration
    const SAMPLES = 200;
    const integrate = (fn: (x: number) => number, xMin: number, xMax: number): number => {
      const h = (xMax - xMin) / SAMPLES;
      let sum = (fn(xMin) + fn(xMax)) / 2;
      for (let i = 1; i < SAMPLES; i++) {
        sum += fn(xMin + i * h);
      }
      return sum * h;
    };

    return {
      id,
      type: 'functionArea',
      objects,
      getParams: () => ({ xStart: getXStart(), xEnd: getXEnd() }),
      setParams: (newParams) => {
        if (typeof params.xStart === 'number' && newParams['xStart'] !== undefined) {
          mutableXStart = newParams['xStart'];
        }
        if (typeof params.xEnd === 'number' && newParams['xEnd'] !== undefined) {
          mutableXEnd = newParams['xEnd'];
        }
        board.update();
      },
      getComputedValues: () => ({
        signedArea: () => {
          const { xMin, xMax, reversed } = getBounds();
          const val = integrate(parentFn.evaluate, xMin, xMax);
          return reversed ? -val : val;
        },
        totalArea: () => {
          const { xMin, xMax } = getBounds();
          return integrate((x) => Math.abs(parentFn.evaluate(x)), xMin, xMax);
        },
        area: () => {
          const { xMin, xMax } = getBounds();
          return integrate((x) => Math.abs(parentFn.evaluate(x)), xMin, xMax);
        },
      }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
